import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { logger, monitoring } from 'common/services';
import { assert } from 'common/utils/assert';
import { generateDynamicPath } from 'common/utils/routing';
import { toBoolean } from 'common/utils/toBoolean';
import { selectHasPrivilege } from 'community/api';
import { EventStatus, MeetingGuest } from 'domain/event';
import { UserPrivilegeName } from 'domain/UserProfile';
import { clearEventStarted } from 'event/live/utils';
import { mapToMeetingGuestFromApi } from 'event/mappers';
import i18n from 'i18n';
import { every, omit } from 'lodash-es';
import {
  addAppAlertDialog,
  addAppErrorMessage,
  addBroadcastInfoMessage,
  removeAppMessage,
  selectAlertById,
} from 'modules/alerts/state';
import { AppRoutes } from 'modules/router';
import { eventRoomActions } from 'pages/EventRoom/state';
import { push } from 'redux-first-history';
import { eventApi, eventSeriesApi, selectActiveEventDetails, selectIsEventOwner } from 'store/apis/event';
import { getEventDetailsCacheIds } from 'store/apis/event/utils';
import { initMergedDataDispatch } from 'store/utils/throttledDispatch';
import { selectUserAccountId } from 'user/api';

import {
  addSupportRequestedMessage,
  audienceJoinedList_WS,
  audienceJoined_WS,
  backstageActions,
  bmmMeetingGuestHandDown_WS,
  broadcastActions,
  broadcastDemoted_WS,
  broadcastGuestDemoted_WS,
  broadcastGuestPromoted_WS,
  broadcastStarted_WS,
  broadcastStopped_WS,
  broadcastVideoViaUrlPlayed_WS,
  broadcastVideoViaUrlStopped_WS,
  dismissRateNatterExperienceAfterTimeout,
  endedEventConfirmClicked,
  eventEnded,
  eventStarted_WS,
  eventStopped_WS,
  guestExitedMeeting_WS,
  meetingConnectionIssueDetected,
  meetingGuestHandDown_WS,
  meetingGuestHandLoweredBy_WS,
  meetingGuestHandUp_WS,
  meetingJoin_WS,
  natteringJoin_WS,
  natteringSkip_WS,
  natteringStarted_WS,
  rateNatterExperienceRequested,
  recordingStarted_WS,
  recordingStopped_WS,
  sendHandDown_WS,
  sendHandUp_WS,
  stageInvitationRejected_WS,
  submitCallFeedback,
} from '../actions';
import { BroadcastNotifications } from '../constants';
import { displayHandRaisedAlert, updateHandRaisedAlert, userDemotedEffect } from '../effects/meeting.effects';
import { eventSlice } from '../eventSlice';
import {
  addAudience,
  addParticipantsWithRaisedHand,
  removeParticipantsWithRaisedHand,
  removeUserFromAudience,
  setCloudPlayer,
  setDisconnectedParticipants,
} from '../eventSlice.actions';
import {
  selectCurrentEventPhase,
  selectEventHosts,
  selectIsCurrentUserEventHost,
  selectIsUserEventHost,
  selectPinValue,
  selectRoomIdToRate,
} from '../selectors';
import { EventPhase } from '../types';
import { getNotificationRecentSpeakersCount } from '../utils';

import type { RootState } from 'store';

let groupBroadcastAudience: ReturnType<
  typeof initMergedDataDispatch<MeetingGuest, { getState: () => RootState }>
> | null = null;

const getGroupBroadcastAudience = (): ReturnType<
  typeof initMergedDataDispatch<MeetingGuest, { getState: () => RootState }>
> => {
  if (!groupBroadcastAudience) {
    groupBroadcastAudience = initMergedDataDispatch<MeetingGuest, { getState: () => RootState }>(
      (tmpBroadcastAudience, { dispatch, getState }) => {
        const { participantsDisconnected, isBigMeetingMode } = getState().event;
        if (isBigMeetingMode) return;

        dispatch(addAudience(tmpBroadcastAudience));
        const tmpBroadcastAudienceIds = tmpBroadcastAudience.map((a) => a.id);
        const updatedDisconnectedParticipants = participantsDisconnected.filter(
          (id) => !tmpBroadcastAudienceIds.includes(id)
        );
        dispatch(setDisconnectedParticipants(updatedDisconnectedParticipants));
      },
      { shouldAddDataToFront: true }
    );
  }
  return groupBroadcastAudience;
};

export const meetingListener = createListenerMiddleware<RootState>();

meetingListener.startListening({
  actionCreator: broadcastVideoViaUrlPlayed_WS,
  effect: ({ payload }, { getState, dispatch }) => {
    const state = getState();
    const userAccountId = selectUserAccountId(state);
    const isUser = userAccountId === payload.createdById;

    dispatch(setCloudPlayer(payload));

    if (selectCurrentEventPhase(state) !== EventPhase.Broadcast) return;

    dispatch(addBroadcastInfoMessage(`${isUser ? 'You' : payload.createdByName} started sharing a video`));
  },
});

meetingListener.startListening({
  actionCreator: broadcastVideoViaUrlStopped_WS,
  effect: ({ payload }, { getState, dispatch }) => {
    if (payload?.message) {
      dispatch(
        addAppErrorMessage({
          metadata: 'BroadcastService.stopVideoViaUrl',
          message: `Unable to stop video: ${payload.message}`,
        })
      );
    } else {
      const state = getState();
      if (selectCurrentEventPhase(state) !== EventPhase.Broadcast) return;

      const cloudPlayer = state.event.broadcast?.cloudPlayer;
      if (!cloudPlayer) return;

      const userAccountId = selectUserAccountId(state);
      const isStoppedByCurrentUser = userAccountId === payload.callerUserId;
      if (isStoppedByCurrentUser) {
        dispatch(addBroadcastInfoMessage('You stopped sharing the video'));
      } else {
        const stoppedBy = state.event.speakers.find(({ id }) => id === payload.callerUserId);
        dispatch(addBroadcastInfoMessage(`${stoppedBy ? stoppedBy.name : 'Someone'} stopped the video`));
      }
    }
    dispatch(setCloudPlayer(null));
  },
});

meetingListener.startListening({
  actionCreator: stageInvitationRejected_WS,
  effect: ({ payload }, { getState, dispatch }) => {
    const state = getState();

    if (selectCurrentEventPhase(state) !== EventPhase.Broadcast) return;

    const { invitedUserFullName, invitingUserId } = payload;
    const userAccountId = selectUserAccountId(state);
    const isUser = userAccountId === Number(invitingUserId);
    if (isUser) {
      dispatch(addBroadcastInfoMessage(`${invitedUserFullName} has declined your invitation`));
    }
  },
});

meetingListener.startListening({
  actionCreator: audienceJoined_WS,
  effect: ({ payload }, { getState, dispatch }) => {
    const tmpMeetingGuest = mapToMeetingGuestFromApi(payload);

    getGroupBroadcastAudience()([tmpMeetingGuest], { dispatch, getState });
  },
});

meetingListener.startListening({
  actionCreator: broadcastGuestDemoted_WS,
  effect: ({ payload }, { getState, dispatch }) => {
    const state = getState();
    const speaker = mapToMeetingGuestFromApi(payload);
    const userAccountId = selectUserAccountId(state);
    const isUser = userAccountId === speaker.id;

    if (isUser) {
      dispatch(
        addBroadcastInfoMessage({
          message: BroadcastNotifications.currentUserLeftStage.getMessage(),
          id: BroadcastNotifications.currentUserLeftStage.id,
        })
      );
      return;
    }

    if (selectCurrentEventPhase(state) !== EventPhase.Broadcast) return;

    const previousNotification = selectAlertById(BroadcastNotifications.otherUserLeftStage.id)(state);
    const recentSpeakersCount = getNotificationRecentSpeakersCount(previousNotification);

    dispatch(
      addBroadcastInfoMessage({
        message: BroadcastNotifications.otherUserLeftStage.getMessage(speaker.name, recentSpeakersCount),
        id: BroadcastNotifications.otherUserLeftStage.id,
        metadata: recentSpeakersCount,
      })
    );
  },
});

meetingListener.startListening({
  actionCreator: guestExitedMeeting_WS,
  effect: ({ payload }, { dispatch, getOriginalState, getState }) => {
    const state = getState();
    const userAccountId = selectUserAccountId(state);
    const speaker = mapToMeetingGuestFromApi(payload);
    const isUser = userAccountId === speaker.id;
    if (isUser) {
      const eventPinValue = selectPinValue(state);

      // fix for https://natterco.atlassian.net/browse/NAT-4798
      dispatch(
        meetingJoin_WS({
          pin: eventPinValue,
        })
      );
      return;
    }

    if (selectCurrentEventPhase(state) !== EventPhase.Broadcast) return;

    const isSpeaker = getOriginalState().event.speakers.some((s) => s.id === speaker.id);
    if (!isSpeaker) return;

    const previousNotification = selectAlertById(BroadcastNotifications.otherUserLeftStage.id)(state);
    const recentSpeakersCount = getNotificationRecentSpeakersCount(previousNotification);

    dispatch(
      addBroadcastInfoMessage({
        message: BroadcastNotifications.otherUserLeftStage.getMessage(speaker.name, recentSpeakersCount),
        id: BroadcastNotifications.otherUserLeftStage.id,
        metadata: recentSpeakersCount,
      })
    );
  },
});

meetingListener.startListening({
  actionCreator: broadcastGuestPromoted_WS,
  effect: ({ payload }, { getState, dispatch }) => {
    const state = getState();

    const speaker = mapToMeetingGuestFromApi(payload);
    dispatch(broadcastActions.promotedToSpeaker(speaker));

    const userAccountId = selectUserAccountId(state);
    if (!userAccountId) return;

    const isUser = userAccountId === speaker.id;
    if (isUser) {
      return;
    }

    if (selectCurrentEventPhase(state) !== EventPhase.Broadcast) return;

    const previousNotification = selectAlertById(BroadcastNotifications.otherUserJoinedStage.id)(state);
    const recentSpeakersCount = getNotificationRecentSpeakersCount(previousNotification);

    dispatch(
      addBroadcastInfoMessage({
        message: BroadcastNotifications.otherUserJoinedStage.getMessage(speaker.name, recentSpeakersCount),
        id: BroadcastNotifications.otherUserJoinedStage.id,
        metadata: recentSpeakersCount,
      })
    );
  },
});

meetingListener.startListening({
  actionCreator: audienceJoinedList_WS,
  effect: ({ payload }, { dispatch }) => {
    const newAudienceMembers = payload.map(mapToMeetingGuestFromApi);
    dispatch(addAudience(newAudienceMembers));

    const newParticipantsWithRaisedHand = payload.filter(({ handRaised }) => handRaised).map(({ id }) => Number(id));
    dispatch(addParticipantsWithRaisedHand(newParticipantsWithRaisedHand));
  },
});

meetingListener.startListening({
  actionCreator: eventStarted_WS,
  effect: ({ payload }, { dispatch, getState }) => {
    if (!payload.eventId) return;
    getEventDetailsCacheIds(getState(), payload.eventId).forEach((id) =>
      dispatch(
        eventApi.util.updateQueryData('getEventDetails', { id }, (data) => {
          data.eventTime.startTime = new Date().toISOString();
          data.status = EventStatus.Started;
        })
      )
    );
  },
});

meetingListener.startListening({
  actionCreator: eventStopped_WS,
  effect: ({ payload }, { dispatch, getState }) => {
    if (!payload.eventId) return;
    // The event is over, so the host's "I just started this" marker must not outlive it — otherwise a
    // later visit to the join page in the same tab would be judged by the marker, not the server.
    clearEventStarted(payload.eventId);
    getEventDetailsCacheIds(getState(), payload.eventId).forEach((id) =>
      dispatch(
        eventApi.util.updateQueryData('getEventDetails', { id }, (data) => {
          data.status = EventStatus.Finished;
        })
      )
    );
  },
});

let dismissRateNatterExperienceTimeout: NodeJS.Timeout;
meetingListener.startListening({
  actionCreator: broadcastStarted_WS,
  effect: ({ payload }, { dispatch, getState, getOriginalState }) => {
    logger.info('[meetingListener] Broadcast Started', {
      ...omit(payload, ['accessToken', 'broadcastChatToken']),
    });
    if (toBoolean(payload.isRecording)) {
      dispatch(addBroadcastInfoMessage('This Event is being recorded'));
    }

    const roomIdToRate = selectRoomIdToRate(getState());
    if (roomIdToRate) {
      dismissRateNatterExperienceTimeout = setTimeout(() => {
        dispatch(dismissRateNatterExperienceAfterTimeout());
      }, 20_000);
    }

    const isHost = selectIsCurrentUserEventHost(getState());
    const { event } = getOriginalState();
    if (event.eventPhase === EventPhase.Nattering && isHost) {
      dispatch(addBroadcastInfoMessage('You’re back in the Broadcast as a Host'));
    }
    dispatch(removeAppMessage(addSupportRequestedMessage.payload.id));
  },
});

meetingListener.startListening({
  actionCreator: broadcastStopped_WS,
  effect: (_, { dispatch, getState }) => {
    const state = getState();
    const isHost = selectIsCurrentUserEventHost(state);
    if (isHost) {
      const hosts = selectEventHosts(state) ?? [];
      dispatch(broadcastActions.backstageStarted({ hosts }));
      dispatch(addBroadcastInfoMessage('You’re now in the Backstage'));
    } else {
      dispatch(broadcastActions.broadcastStopped());
    }
  },
});

meetingListener.startListening({
  actionCreator: natteringStarted_WS,
  effect: ({ payload }, { dispatch, getState }) => {
    logger.info('[meetingListener] Nattering Started', { ...payload });
    if (dismissRateNatterExperienceTimeout) {
      clearTimeout(dismissRateNatterExperienceTimeout);
    }

    const isHost = selectIsCurrentUserEventHost(getState());
    if (!isHost) {
      dispatch(broadcastActions.natteringStarted());
    }
  },
});

meetingListener.startListening({
  actionCreator: meetingGuestHandLoweredBy_WS,
  effect: ({ payload: { loweredByUserName } }, { dispatch }) => {
    dispatch(addBroadcastInfoMessage(`${loweredByUserName} has lowered your hand`));
  },
});

meetingListener.startListening({
  actionCreator: recordingStarted_WS,
  effect: (_, { dispatch }) => {
    dispatch(addBroadcastInfoMessage('This Event is being recorded'));
  },
});

meetingListener.startListening({
  actionCreator: recordingStopped_WS,
  effect: (_, { dispatch }) => {
    dispatch(addBroadcastInfoMessage('Recording has stopped'));
  },
});

meetingListener.startListening({
  matcher: isAnyOf(meetingGuestHandUp_WS, addParticipantsWithRaisedHand),
  effect: displayHandRaisedAlert,
});

meetingListener.startListening({
  actionCreator: meetingGuestHandDown_WS,
  effect: updateHandRaisedAlert,
});

meetingListener.startListening({
  actionCreator: broadcastDemoted_WS,
  effect: userDemotedEffect,
});

meetingListener.startListening({
  actionCreator: bmmMeetingGuestHandDown_WS,
  effect: ({ payload }, { getState, dispatch }) => {
    const state = getState();
    const guest = mapToMeetingGuestFromApi(payload);
    const isUserHost = selectIsUserEventHost(state, guest.id);
    if (isUserHost) return;

    dispatch(removeUserFromAudience(guest));
  },
});

/**
 * This fixes:
 * Audience users from the big meeting mode don't have any indicator that the hand is raised after rasing a hand
 * https://natterco.atlassian.net/browse/NT-397
 */
meetingListener.startListening({
  matcher: isAnyOf(sendHandUp_WS.action, sendHandDown_WS.action, meetingGuestHandLoweredBy_WS),
  effect: ({ type }, { getState, dispatch }) => {
    const state = getState();
    if (!state.event.isBigMeetingMode) return;

    const isCurrentUserHost = selectIsCurrentUserEventHost(state);
    if (isCurrentUserHost) return;

    const userAccountId = selectUserAccountId(state);
    if (!userAccountId) return;

    if (type === sendHandUp_WS.action.type) {
      dispatch(addParticipantsWithRaisedHand([userAccountId]));
    } else {
      dispatch(removeParticipantsWithRaisedHand([userAccountId]));
    }
  },
});

meetingListener.startListening({
  actionCreator: natteringSkip_WS.action,
  effect: () => {
    logger.info('[meetingListener] nattering skipped');
    monitoring.trackAction('broadcast.user_skipped_nattering');
  },
});

meetingListener.startListening({
  actionCreator: natteringJoin_WS.action,
  effect: () => {
    logger.info('[meetingListener] nattering accepted');
    monitoring.trackAction('broadcast.user_joined_nattering');
  },
});

meetingListener.startListening({
  actionCreator: meetingConnectionIssueDetected,
  effect: ({ payload: error }, { dispatch }) => {
    logger.error('[meetingListener] meeting connection issue detected', error);
    monitoring.trackAction('broadcast.user_failed_to_join', { error });
    dispatch(addAppErrorMessage('Failed to join the channel'));
  },
});

meetingListener.startListening({
  actionCreator: submitCallFeedback.fulfilled,
  effect: ({ meta }, { dispatch }) => {
    logger.info('[meetingListener] Rate Natter Experience submitted');
    monitoring.trackAction('broadcast.room_feedback', { rate: meta.arg.rate });
    dispatch(addBroadcastInfoMessage(i18n.t('event:broadcast.rateNatterExperienceSuccess')));
  },
});

meetingListener.startListening({
  actionCreator: rateNatterExperienceRequested,
  effect: () => {
    logger.info('[meetingListener] Rate Natter Experience requested');
    monitoring.trackAction('broadcast.room_feedback_requested');
  },
});

meetingListener.startListening({
  actionCreator: eventEnded,
  effect: ({ payload: { isCurrentUserHost } }, { dispatch, getState }) => {
    const state = getState();
    const eventDetails = selectActiveEventDetails(state);
    assert(eventDetails);

    const { id, publicId } = eventDetails;
    // The host lands on the private event page, which is keyed by the UUID publicId (HNS-148); the
    // numeric id is the legacy fallback. Non-hosts go to the live EventEnd route, which stays numeric
    // (its container redirects on to the UUID details URL).
    dispatch(
      push(
        isCurrentUserHost
          ? generateDynamicPath(AppRoutes.EventDetails, { id: publicId ?? id })
          : generateDynamicPath(AppRoutes.EventEnd, { id })
      )
    );
  },
});

meetingListener.startListening({
  actionCreator: eventEnded,
  effect: async (_, { dispatch, getState }) => {
    const state = getState();
    const eventDetails = selectActiveEventDetails(state);
    assert(eventDetails);
    const { seriesId } = eventDetails;
    const isOwned = selectIsEventOwner(state); // Modal should be visible only for the real owner
    const canCreateReport = isOwned && selectHasPrivilege(UserPrivilegeName.MANAGE_REPORTS)(state);
    if (!canCreateReport) return;

    let isLastInSeries = false;
    let eventName = eventDetails.name;
    if (seriesId) {
      const seriesDetails = await dispatch(
        eventSeriesApi.endpoints.getEventSeriesDetails.initiate({ seriesId }, {})
      ).unwrap();
      isLastInSeries = every(seriesDetails.events, { state: EventStatus.Finished });
      eventName = seriesDetails.name;
    }

    const context = seriesId ? (isLastInSeries ? 'lastInSeries' : 'series') : '';
    dispatch(
      addAppAlertDialog({
        title: i18n.t('event:eventEndedModal.title', { context }),
        description: i18n.t('event:eventEndedModal.description', { context, eventName }),
        confirmLabel: i18n.t('event:eventEndedModal.confirm'),
        cancelLabel: i18n.t('event:eventEndedModal.close'),
        showCancelButton: true,
        actionToDispatchOnConfirm: endedEventConfirmClicked(),
      })
    );
  },
});

meetingListener.startListening({
  actionCreator: endedEventConfirmClicked,
  effect: (_, { dispatch }) => {
    dispatch(push(AppRoutes.InsightsReports + AppRoutes.InsightsReportsEvents));
  },
});

meetingListener.startListening({
  actionCreator: backstageActions.viewRequestsClicked,
  effect: () => {
    logger.info('[meetingListener] View requests clicked');
    monitoring.trackAction('backstage.view_requests_clicked');
  },
});

meetingListener.startListening({
  matcher: eventApi.endpoints.getMeetingMetrics.matchFulfilled,
  effect: ({ payload }, { dispatch, getState }) => {
    const state = getState();
    const currentSequenceId = state.event.metrics.sequenceId;

    // Ignore if sequenceId is not newer
    if (payload.sequenceId <= currentSequenceId) {
      return;
    }

    // Only dispatch the fields that were actually returned from the API
    // This avoids overwriting concurrent WebSocket updates with stale values
    dispatch(
      eventSlice.actions.meetingMetricsUpdated({
        activeParticipants: payload.activeParticipants,
        sequenceId: payload.sequenceId,
      })
    );
  },
});

meetingListener.startListening({
  matcher: eventApi.endpoints.getMeetingMetrics.matchRejected,
  effect: ({ error }) => {
    // Log error but don't show to user
    logger.error('[meetingListener] Failed to fetch meeting metrics', error);
  },
});

meetingListener.startListening({
  actionCreator: eventRoomActions.cleanedUp,
  effect: () => {
    // Reset the audience batching handler to prevent stale state in React 18 Strict Mode
    groupBroadcastAudience = null;
  },
});
